import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import Table from "components/Table";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import sessionHelper from "services/sessionHelper";
import { getPlayStyles } from "services/playStyleServices";
import { PermittedRoles } from "utils/permissions";
import { PLAY_TYPE_NAMES } from "./PlayStyleEditor";

const useStyles = makeStyles(() => ({
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  preview: {
    width: 72,
    height: 72,
    objectFit: "contain",
    background:
      "repeating-conic-gradient(#eee 0% 25%, #fafafa 0% 50%) 50% / 12px 12px",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    padding: 3,
  },
  previewEmpty: {
    width: 72,
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed #cfcfcf",
    borderRadius: 6,
    color: "#bdbdbd",
    margin: "0 auto",
  },
}));

const ImageThumb = ({ url, label }: { url?: string; label: string }) => {
  const styles = useStyles();
  const [errored, setErrored] = useState(false);
  const busted = url ? `${url.split("?")[0]}?t=${Date.now()}` : url;

  return url && !errored ? (
    <img
      src={busted}
      alt={label}
      className={styles.preview}
      onError={() => setErrored(true)}
    />
  ) : (
    <Box className={styles.previewEmpty}>
      <BrokenImageOutlinedIcon fontSize="small" />
    </Box>
  );
};

interface RowActionsProps {
  data: any;
}

const RowActions = ({ data }: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEditClick = (event: any, rowData: any) => {
    handleClose(event);
    navigate(configData.routes.playStyles.edit?.replace(":id", rowData?._id));
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={(e) => handleEditClick(e, data)}>Edit</MenuItem>
      </Menu>
    </>
  );
};

const columns = [
  {
    id: "order",
    label: "Order",
    type: "number",
    isSortable: false,
    alignHead: "center",
    alignBody: "center",
    renderCell: (data: any) => (
      <Typography variant="body2">{data.order ?? "—"}</Typography>
    ),
  },
  {
    id: "playType",
    label: "Play Type",
    type: "string",
    isSortable: true,
    alignHead: "center",
    alignBody: "center",
    renderCell: (data: any) => (
      <Typography variant="body2">{data.playType || "—"}</Typography>
    ),
  },
  {
    id: "preview",
    label: "Image",
    isSortable: false,
    alignHead: "center",
    alignBody: "center",
    renderCell: (data: any) => (
      <ImageThumb url={data.imageUrl} label={data.playType} />
    ),
  },
  { id: "actions", label: "Actions" },
];

const PlayStyles = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState<any[]>([]);

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLAY_STYLE.UPDATE_ROLES,
  );

  const navigateToCreate = () => navigate(configData.routes.playStyles.create);

  const rowHref = (data: any) =>
    hasEditPermission
      ? configData.routes.playStyles.edit?.replace(":id", data?._id)
      : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getPlayStyles();
      setListData(resp?.data || []);
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredColumns = columns.filter(
    (col) => col.id !== "actions" || hasEditPermission,
  );

  return (
    <ContentShell
      title="Play Styles"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          {sessionHelper.hasRole(PermittedRoles.PLAY_STYLE.CREATE_ROLES) &&
            listData.length < PLAY_TYPE_NAMES.length && (
              <CustomButton onClick={navigateToCreate}>Create</CustomButton>
            )}
        </Box>
      }
    >
      <Box className={styles.container}>
        <Table
          columns={filteredColumns}
          data={listData}
          hidePagination={true}
          highlightAltRow={true}
          actions={RowActions}
          rowHref={rowHref}
        />
      </Box>
    </ContentShell>
  );
};

export default PlayStyles;
